import { getDomainV1 } from "src/apis/getDomainV1";
import { similarity } from "src/generic/utils/text";
import { TDropItem } from "src/types";
export interface IMsReferenceConfig {
  host: string;
  apiKey: string;
}

interface IProvinceSearch {
  countryIsoCd?: string;
  name?: string;
}

export interface IMsReferenceService {
  getCountries: () => Promise<TDropItem[] | undefined>;
  getSubTypes: () => Promise<ISubKeyItem[]>;
  getTypes: () => Promise<IKeyItem[]>;
  getSchoolSubTypes: () => Promise<IKeyItem[]>;
  getSchoolLevels: () => Promise<IKeyItem[]>;
  getInstLevels: () => Promise<IKeyItem[]>;
  getRegions: () => Promise<IRegion[]>;
  searchProvinces: (params: IProvinceSearch) => Promise<IRegion[]>;
  findProvince: (province: string) => Promise<IRegion | undefined>;
  flush: () => void;
}

interface IRefResponse<T> {
  cdRefList: T[];
}

interface IAttribute {
  name: string;
  value: string;
}

interface IRefItem {
  activeInd: "Y" | "N";
  code: string;
  descr: string;
  displayInd: "Y" | "N";
  sortOrder: number;
  attributes: IAttribute[];
}

export interface IKeyItem {
  key: string;
  label: string;
  active: boolean;
}

export interface ISubKeyItem extends IKeyItem {
  parentId: number | string;
}

export interface IRegion extends IKeyItem {
  countryIsoCd: string | undefined;
  regionType: string | undefined;
}

interface ICache {
  subTypes: ISubKeyItem[];
  types: IKeyItem[];
  
  regions: IRegion[];

  schoolSubTypes: IKeyItem[];

  schoolLevels: IKeyItem[];

  instLevels: IKeyItem[];

  countries?: TDropItem[];
}

export const MsReferenceService = (): IMsReferenceService => {
  let cache: ICache = {
    subTypes: [],
    types: [],
    regions: [],
    schoolSubTypes: [],
    schoolLevels: [],
    instLevels: []
  };


  const getRegions = async (): Promise<IRegion[]> => {
    if (cache.regions.length > 0) {
      return new Promise((resolve) => {
        resolve(cache.regions);
      });
    }

     const refs: IRefItem[] = await getDomainV1('region');
      if (refs) {
        const items = refs.map((item) => {
          const country = item.attributes.find((item) => item.name === 'country_iso_cd')?.value || undefined;
          const regionType = item.attributes.find((item) => item.name === 'region_type')?.value || undefined;
          return {
            key: item.code,
            label: item.descr,
            active: item.activeInd === 'Y' ? true : false,
            countryIsoCd: country,
            regionType: regionType
          };
        });
        cache.regions = items;
        return items;
      }
      throw new Error("The expected region payload was not returned from API.");
  };

  const searchProvinces = async (params: IProvinceSearch) => {
    let regions = await getRegions();
    const { name, countryIsoCd } = params;
    if (countryIsoCd) {
      regions = regions.filter((item) => { return item.countryIsoCd === countryIsoCd; });
    }

    if (name) {
      regions = regions.filter((item) => {
        const a = item.label.toLowerCase();
        const b = name.toLowerCase();
        const percent = similarity(a, b);
        return (a.includes(b) || percent > 0.6);
      });
    }
    return regions;
  };

  return {
    flush: () => {
      cache = {
        subTypes: [],
        types: [],
        regions: [],
        schoolSubTypes: [],
        schoolLevels: [],
        instLevels: []
      };
    },
    findProvince: async (province: string) => {
      return (await searchProvinces({})).find((item) => {
        return item.label === province || item.key === province;
      });
    },
    searchProvinces: searchProvinces,
    getRegions: getRegions,
    getSubTypes: async () => {
      if (cache.subTypes.length > 0) {
        return new Promise((resolve) => {
          resolve(cache.subTypes);
        });
      }

      const refs: IRefItem[] = await getDomainV1('org-sub-type');
        if (refs) {
          const items = refs.map((item) => {
            const parentId = parseInt(item.attributes.find((item) => item.name === 'org_type_cd')?.value || '0');
            return {
              key: item.code,
              label: item.descr,
              active: item.activeInd === 'Y' ? true : false,
              parentId: isNaN(parentId) ? 0 : parentId,
            };
          });
          cache.subTypes = items;
          return items;
        }
        throw new Error("The expected payload was not returned from API.");
    },
    getTypes: async () => {
      if (cache.types.length > 0) {
        return new Promise((resolve) => {
          resolve(cache.types);
        });
      }

      const refs: IRefItem[] = await getDomainV1('org-type');
        if (refs) {
          const items = refs.map((item) => {
            return {
              key: item.code,
              label: item.descr,
              active: item.activeInd === 'Y' ? true : false
            };
          });
          cache.types = items;
          return items;
        }
        throw new Error("The expected payload was not returned from API.");
    },
    getInstLevels: async () => {
      if (cache.instLevels.length > 0) {
        return new Promise((resolve) => {
          resolve(cache.instLevels);
        });
      }

      const refs: IRefItem[] = await getDomainV1('inst-lvl');
        if (refs) {
          const items = refs.map((item) => {
            return {
              key: item.code,
              label: item.descr,
              active: item.activeInd === 'Y' ? true : false
            };
          });
          cache.instLevels = items;
          return items;
        }
        throw new Error("The expected institution levels payload was not returned from API.");
    },
    getSchoolSubTypes: async () => {
      if (cache.schoolSubTypes.length > 0) {
        return new Promise((resolve) => {
          resolve(cache.schoolSubTypes);
        });
      }

      const refs: IRefItem[] = await getDomainV1('school-sub-type');
        if (refs) {
          const items = refs.map((item) => {
            return {
              key: item.code,
              label: item.descr,
              active: item.activeInd === 'Y' ? true : false
            };
          });
          cache.schoolSubTypes = items;
          return items;
        }
        throw new Error("The expected school-sub-type payload was not returned from API.");
    },
    getSchoolLevels: async () => {
      if (cache.schoolLevels.length > 0) {
        return new Promise((resolve) => {
          resolve(cache.schoolLevels);
        });
      }

      const refs: IRefItem[] = await getDomainV1('school-level');
        if (refs) {
          const items = refs.map((item) => {
            return {
              key: item.code,
              label: item.descr,
              active: item.activeInd === 'Y' ? true : false
            };
          });
          cache.schoolLevels = items;
          return items;
        }
        throw new Error("The expected school-level payload was not returned from API.");
    },
    getCountries: async () => {
      //API handles processing msref response
      if (cache.countries && cache.countries.length > 0) return cache.countries
      const countries = await getDomainV1('country');
      cache.countries = countries;
      return countries
    }
  };
};